<template>
  <div :class="rootClass" :style="rootStyle" :id="rendering?.dataSource">
    <ContentGrid :desktop-default-grid="[2, 20]" :mobile-default-grid="[1, 14]">
      <JssRichText class="resource-card-grid-title" :field="fields?.title" tag="h2" />
      <JssRichText class="resource-card-grid-desc" :field="fields?.subtitle" />
      <div
        :class="['resource-card-grid-content', contentCountClass]"
        v-if="fields?.list?.length > 0"
      >
        <siteLink
          v-for="(item, index) in fields?.list"
          :key="index"
          class="card-item"
          :link="item.fields?.button?.fields?.link"
        >
          <AdaptiveImage
            class="card-image"
            is-background
            v-if="item.fields?.image"
            :field="item.fields?.image"
            :style="{ aspectRatio: fields?.aspectRatio?.fields?.phrase?.value || '3/2' }"
          >
            <JssRichText
              class="card-tag"
              :field="item?.fields?.tag"
              :style="{ backgroundColor: item?.fields?.tagBgColor?.value }"
            />
          </AdaptiveImage>
          <div class="card-content">
            <JssRichText class="card-subtitle" :field="item?.fields?.subtitle" tag="h4" />
            <JssRichText class="card-title" :field="item?.fields?.title" tag="h3" />
            <JssRichText class="card-desc" :field="item?.fields?.desc" />
          </div>
          <div class="card-button">
            <SiteButton
              v-bind="item?.fields?.button"
              :open-link="!fields?.openModal?.value"
              :open-download="!fields?.openModal?.value"
              @click="(event) => onButtonClick(event, item, fields?.openModal?.value)"
            />
          </div>
        </siteLink>
      </div>
    </ContentGrid>
  </div>
</template>

<script>
import { ref, computed, inject, onMounted } from 'vue';
import BUS_EVENTS from '@/utils/bus-events';
import { setLeadFormSubmitted, isLeadFormSubmitted } from '@/utils/form-utils';
import utils from '@/utils';
export default {
  name: 'ResourceCardGrid',
  props: {
    fields: {
      type: Object,
      default: () => ({}),
    },
    rendering: {
      type: Object,
    },
  },
  setup(props) {
    const bus = inject('$bus');
    let isSubmitted = ref(false);

    const rootClass = computed(() =>
      utils.overall.baseComClass(props.fields, 'resource-card-grid')
    );

    const rootStyle = computed(() => {
      const result = utils.overall.baseComStyle(props.fields);
      return result;
    });

    const contentCountClass = computed(() => {
      const count = props.fields?.list?.length || 0;
      if (count === 2) return 'is-count-2';
      if (count === 3) return 'is-count-3';
      return 'is-count-multi';
    });

    const download = (item) => {
      const url = item?.fields?.file?.value?.src;
      if (!url) return;

      const fileName = item?.fields?.file?.value?.name || 'download';
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

    const performButtonAction = (item) => {
      const buttonFields = item?.fields?.button?.fields;
      const href = buttonFields?.link?.value?.href;
      const fileSrc = buttonFields?.file?.value?.src;
      const modeview = buttonFields?.modeview?.value;
      if (href) {
        window.open(href, '_blank');
      } else if (fileSrc) {
        if (modeview) {
          window.open(fileSrc, '_blank');
        } else {
          download(item?.fields?.button);
        }
      }
    };

    const onButtonClick = (event, item, openModal) => {
      event.preventDefault();
      if (!openModal) {
        return;
      }
      if (isSubmitted.value) {
        performButtonAction(item);
        return;
      }
      bus?.emit(BUS_EVENTS.FORM_OPEN, {
        onSuccess: () => {
          setLeadFormSubmitted('resourceCard');
          isSubmitted.value = true;
          performButtonAction(item);
        },
      });
    };

    onMounted(() => {
      if (isLeadFormSubmitted('resourceCard')) {
        isSubmitted.value = true;
      } else {
        isSubmitted.value = false;
      }
    });

    return {
      rootClass,
      rootStyle,
      contentCountClass,
      isSubmitted,
      onButtonClick,
      download,
    };
  },
};
</script>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.resource-card-grid {
  position: relative;
  width: 100%;
  padding: 40px 0;

  &-title {
    text-align: center;
  }

  &-desc {
    font-size: 14px;
    line-height: 1.6;
    color: $black;
    margin-top: 8px;
  }

  &-content {
    width: 100%;
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    gap: 20px;
    margin-top: 24px;

    .card-item {
      width: 100%;
      background: $white;
      border-radius: 12px;
      overflow: hidden;
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
      display: flex;
      flex-direction: column;
      padding: 16px;
    }

    .card-image {
      width: 100%;
      aspect-ratio: 3/2;
      border-radius: 8px;
      overflow: hidden;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }

    .card-tag {
      font-size: 14px;
      line-height: 1.4;
      color: $white;
      padding: 4px 16px;
      border-radius: 4px;
      display: inline-block;
      margin: 16px 0 0 16px;
    }

    .card-content {
      flex: 1;
      display: flex;
      flex-direction: column;
      margin-top: 12px;
    }

    .card-subtitle {
      font-size: 12px;
      font-weight: 500;
      margin-bottom: 8px;
    }

    .card-title {
      font-size: 14px;
      font-weight: 600;
      margin-bottom: 8px;
      line-height: 1.4;
    }

    .card-desc {
      font-size: 12px;
      line-height: 1.5;
      flex: 1;
      @include line-clamp(3);
    }

    .card-button {
      margin-top: 12px;
    }
  }

  @include tablet-landscape {
    padding: 60px 0;
    &-content {
      flex-direction: row;
      flex-wrap: wrap;
      gap: 30px;

      .card-item {
        width: calc(33.333% - 20px);
      }

      &.is-count-2 .card-item {
        width: calc(50% - 15px);
      }

      &.is-count-3 .card-item {
        width: calc(33.333% - 20px);
      }

      &.is-count-multi .card-item {
        width: calc(33.333% - 20px);
      }
    }

    &-desc {
      font-size: 18px;
    }

    .card-item {
      border-radius: 16px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
      padding: 24px;
    }

    .card-image {
      aspect-ratio: 3/2;
    }

    .card-content {
      margin-top: 16px;
    }

    .card-subtitle {
      font-size: 14px;
      margin-bottom: 12px;
    }

    .card-title {
      font-size: 16px;
      margin-bottom: 12px;
    }

    .card-desc {
      font-size: 14px;
      line-height: 1.6;
      @include line-clamp(4);
    }

    .card-button {
      margin-top: 16px;
    }
  }
}
</style>
